'use client';

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useRoomContext, useParticipants } from '@livekit/components-react';

export interface HandRaiseQueueItem {
  participantId: string;
  participantName: string;
  timestamp: number;
  isLocal: boolean;
}

interface HandRaiseQueueContextType {
  handRaiseQueue: HandRaiseQueueItem[];
  addToQueue: (participantId: string, participantName: string, timestamp?: number) => void;
  removeFromQueue: (participantId: string) => void;
  clearQueue: () => void;
  getQueuePosition: (participantId: string) => number;
  getQueueCount: () => number;
  isParticipantInQueue: (participantId: string) => boolean;
}

const HandRaiseQueueContext = createContext<HandRaiseQueueContextType | undefined>(undefined);

export function HandRaiseQueueProvider({ children }: { children: React.ReactNode }) {
  const [handRaiseQueue, setHandRaiseQueue] = useState<HandRaiseQueueItem[]>([]);
  const room = useRoomContext();
  const participants = useParticipants();

  const addToQueue = useCallback(
    (participantId: string, participantName: string, timestamp?: number) => {
      setHandRaiseQueue((prev) => {
        // Verificar se já está na fila
        if (prev.some((item) => item.participantId === participantId)) {
          return prev;
        }

        const newItem: HandRaiseQueueItem = {
          participantId,
          participantName,
          timestamp: timestamp || Date.now(),
          isLocal: participantId === room?.localParticipant?.identity,
        };

        // Adicionar na ordem temporal (mais antigo primeiro)
        const newQueue = [...prev, newItem].sort((a, b) => a.timestamp - b.timestamp);
        return newQueue;
      });
    },
    [room?.localParticipant?.identity],
  );

  const removeFromQueue = useCallback((participantId: string) => {
    setHandRaiseQueue((prev) => prev.filter((item) => item.participantId !== participantId));
  }, []);

  const clearQueue = useCallback(() => {
    setHandRaiseQueue([]);
  }, []);

  const getQueuePosition = useCallback(
    (participantId: string) => {
      const index = handRaiseQueue.findIndex((item) => item.participantId === participantId);
      return index === -1 ? -1 : index + 1; // Retorna 1-based position
    },
    [handRaiseQueue],
  );

  const getQueueCount = useCallback(() => {
    return handRaiseQueue.length;
  }, [handRaiseQueue]);

  const isParticipantInQueue = useCallback(
    (participantId: string) => {
      return handRaiseQueue.some((item) => item.participantId === participantId);
    },
    [handRaiseQueue],
  );

  // Limpar participantes desconectados da fila
  useEffect(() => {
    const connectedParticipantIds = new Set(
      [room?.localParticipant?.identity, ...participants.map((p) => p.identity)].filter(Boolean),
    );

    setHandRaiseQueue((prev) =>
      prev.filter((item) => connectedParticipantIds.has(item.participantId)),
    );
  }, [participants, room?.localParticipant?.identity]);

  // Escutar eventos de data channel para sincronizar fila
  useEffect(() => {
    if (!room) return;

    const handleDataReceived = (payload: Uint8Array, participant: any) => {
      try {
        const data = JSON.parse(new TextDecoder().decode(payload));

        if (data.type === 'hand-raise') {
          if (data.raised) {
            // Encontrar nome do participante
            const participantName =
              participants.find((p) => p.identity === data.id)?.name ||
              participants.find((p) => p.identity === data.id)?.identity ||
              data.id;
            addToQueue(data.id, participantName, data.timestamp);
          } else {
            removeFromQueue(data.id);
          }
        }
      } catch (e) {
        console.error('Erro ao processar data channel na fila:', e);
      }
    };

    room.on('dataReceived', handleDataReceived);

    if (room.localParticipant) {
      room.localParticipant.on('dataReceived', handleDataReceived);
    }

    return () => {
      room.off('dataReceived', handleDataReceived);
      if (room.localParticipant) {
        room.localParticipant.off('dataReceived', handleDataReceived);
      }
    };
  }, [room, participants, addToQueue, removeFromQueue]);

  // Sincronizar com metadata dos participantes
  useEffect(() => {
    participants.forEach((participant) => {
      try {
        const metadata = participant.metadata;
        if (metadata) {
          const parsed = JSON.parse(metadata);
          if (parsed.handRaised) {
            const participantName = participant.name || participant.identity;
            addToQueue(participant.identity, participantName, parsed.timestamp);
          } else {
            removeFromQueue(participant.identity);
          }
        }
      } catch (e) {
        // Ignorar erros de parsing
      }
    });
  }, [participants, addToQueue, removeFromQueue]);

  const value: HandRaiseQueueContextType = {
    handRaiseQueue,
    addToQueue,
    removeFromQueue,
    clearQueue,
    getQueuePosition,
    getQueueCount,
    isParticipantInQueue,
  };

  return <HandRaiseQueueContext.Provider value={value}>{children}</HandRaiseQueueContext.Provider>;
}

export function useHandRaiseQueue() {
  const context = useContext(HandRaiseQueueContext);
  if (context === undefined) {
    throw new Error('useHandRaiseQueue must be used within a HandRaiseQueueProvider');
  }
  return context;
}
